import { repositoryForRemote, type Promotion, type ProjectMember, type ProjectSource, type SourceRepository, type WorkItem } from '@aioe/shared';
import { ArrowLeft, ClockArrowRotateLeft, CodeTrunk, Cpu, FileText, Folder, FolderArrowUp, LayoutColumns, ListCheck, Persons, PersonsLock, Shield } from '@gravity-ui/icons';
import { Tooltip } from '@heroui/react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';

import { ApiError, routes } from '../api/client.ts';
import { useAuth } from '../auth/context.ts';
import { groupsAvailable } from '../directory.ts';
import { isCloud } from '../edition.ts';
import { ProjectInbox } from '../components/Inbox.tsx';
import { ProjectMapPanel } from '../components/projectMap.tsx';
import { ProjectPurpose } from '../components/projectPurpose.tsx';
import { ProjectMemoryPanel } from '../components/projectMemory.tsx';
import { KnowledgePanel } from '../components/memoryKnowledge.tsx';
import { LabellingRulesForm, MemoryBrowser, OpenedMemoryItem, ShareMemory } from '../components/memoryBrowser.tsx';
import { MemoryExplorer } from '../components/memoryExplorer/MemoryExplorer.tsx';
import { ClassifyPrompt, MemoryAccessNote } from '../components/memory.tsx';
import { ProjectReaders } from '../components/projectMemoryAccess.tsx';
import { ProjectTools } from '../components/projectTools.tsx';
import { ProjectRisks } from '../components/projectRisks.tsx';
import { ProjectIncidents } from '../components/projectIncidents.tsx';
import { ProjectRuns } from '../components/projectRuns.tsx';
import { ProjectHistory } from '../components/projectHistory.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

/** The 410 a merged-away project answers (routes/projects.ts). */
type MergedAway = { mergedInto: string; mergedIntoName?: string; name?: string };
import { WorkingNow } from '../components/workingNow.tsx';
import { ProjectBoard } from '../components/ProjectBoard.tsx';
import { TeamworkList } from '../components/teamwork/TeamworkList.tsx';
import { PersonAvatar } from '../components/ui/PersonAvatar.tsx';
import { TrackersPanel } from '../components/trackers.tsx';
import { CloudRuns } from '../components/cloudRuns.tsx';
import { A2aAgentsPanel } from '../components/a2aAgents.tsx';
import { SecretsPanel } from '../components/secrets.tsx';
import { QueuePanel } from '../components/queue.tsx';
import { WorkCosts } from '../components/costInsights.tsx';
import { ClassificationPanel, ownClassification } from '../components/classification.tsx';
import { MergeRulesPanel, PullRequestsPanel } from '../components/pullRequests.tsx';
import { ProjectSettingsPolicy } from '../components/projectSettingsPolicy.tsx';
import { ProjectBudgetLine } from './BudgetsPage.tsx';
import { ProjectLifecycle } from '../components/projectLifecycle.tsx';
import { auditSentence } from '../components/events.ts';
import { SubjectName, SubjectPicker } from '../components/SubjectPicker.tsx';
import { ProjectTeamsList } from '../components/teams/TeamsPanel.tsx';
import { RemoveRoot, RootName } from '../components/projectRoots.tsx';
import { computeMachines, ProjectCompute, RepositoryAgentFiles, RepositoryMachines } from '../components/projectCode.tsx';
import { CodeHistory } from '../components/git/CodeHistory.tsx';
import { PROVIDER_TEXT } from '../components/source.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, Stat, StatusMark, Tick, Toggle } from '../components/ui.tsx';
import { SubnavLayout } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { ArtEmpty, VIEW_TONES, ViewHeader } from '../components/ui/Art.tsx';
import { projectNav, projectNavKey, projectTabParams, resolveProjectTab, type ProjectTab } from '../components/projectTabs.tsx';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

/** One entry per repository (a root has a row for each clone of it). */
const uniqueRoots = <T extends { rootId: string }>(roots: T[]): T[] => roots.filter((r, i) => roots.findIndex((x) => x.rootId === r.rootId) === i);

const ROLE_TEXT: Record<string, string> = { owner: 'Owner', contributor: 'Contributor', viewer: 'Viewer' };

export function ProjectPage() {
  const tone = VIEW_TONES.projects!;
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { tab, part } = resolveProjectTab(params);
  const go = (next: ProjectTab, nextPart?: string) => setParams(projectTabParams(next, nextPart), { replace: true });
  const data = useLoad(
    () =>
      routes.project(projectId).catch((e: unknown) => {
        // Merged away: the page says where it went, in one sentence, instead of an error.
        if (e instanceof ApiError && e.status === 410 && (e.body as { error?: string } | undefined)?.error === 'merged') return { merged: e.body as MergedAway };
        throw e;
      }),
    [projectId],
  );
  const source = useLoad(() => routes.projectSource(projectId).catch(() => null), [projectId]);
  // Manage on the organisation's project register (today: administrators) forces a merge.
  const admin = { data: useMay('projects.register', 'manage') };
  const merged = data.data && 'merged' in data.data ? data.data.merged : undefined;
  const d = data.data && !('merged' in data.data) ? data.data : undefined;
  const { account } = useAuth();
  const owner = d?.role === 'owner';
  const canonical = d?.roots.filter((r) => r.canonical) ?? [];
  const rootIds = [...new Set(d?.roots.map((r) => r.rootId) ?? [])];
  const rootNames = useMemo(() => new Map((d?.roots ?? []).flatMap((r) => (r.name ? [[r.rootId, r.name] as const] : []))), [d?.roots]);
  const machines = d ? computeMachines(d.roots, d.compute) : [];
  const page = (
    <PageColumn width="data">
      <ViewHeader
        tone={tone}
        icon={<Folder />}
        // Never the id: while it loads the header says what kind of page this is.
        title={d?.project.name ?? merged?.name ?? (data.loading ? 'Loading project\u2026' : 'Project')}
        blurb={
          d ? (
            <>
              <Tooltip delay={300}>
                <Tooltip.Trigger className="inline cursor-default underline decoration-dotted underline-offset-4" tabIndex={0}>
                  {d.area ? `In ${d.area.name}` : isCloud() ? 'Team project' : 'Not filed under a business area'}
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Project id <span className="mono">{d.project.id}</span>
                </Tooltip.Content>
              </Tooltip>
              {` · you are ${ROLE_TEXT[d.role]?.toLowerCase() ?? d.role}`}
            </>
          ) : undefined
        }
        actions={
          <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-muted hover:text-foreground">
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            All projects
          </Link>
        }
      />
      <ErrorNote error={data.error} />
      {merged && (
        <ArtEmpty tone={tone} icon={<Folder />} title={`${merged.name ?? 'This project'} was merged into ${merged.mergedIntoName ?? 'another project'}`}
          action={
            <Link to={`/projects/${encodeURIComponent(merged.mergedInto)}`} className="text-accent hover:underline">
              Open it
            </Link>
          }
        >
          Its repositories, members, tasks and records moved there; everything you were looking at here is on that project now.
        </ArtEmpty>
      )}
      {d && (
        <>
          {owner && !d.project.classification && d.project.state !== 'archived' && <ClassifyPrompt projectId={projectId} onChange={data.reload} />}
          {tab === 'overview' && (
            <div className="art-stats">
              <Stat tone={tone} icon={<Persons />} label="Members" value={d.members.length} detail={((n) => `${n} ${n === 1 ? 'owner' : 'owners'}`)(d.members.filter((m) => m.role === 'owner').length)} onPress={() => go('people')} />
              <Stat tone={tone} icon={<CodeTrunk />} label="Repositories" value={rootIds.length} detail={canonical.length ? `${canonical.length} on a node members attach to` : `${d.roots.length} ${d.roots.length === 1 ? 'clone' : 'clones'} reported`} onPress={() => go('code', 'repositories')} />
              <Stat tone={tone} icon={<Cpu />} label="Compute" value={machines.length} detail={`${machines.filter((m) => m.assignment).length} nodes, ${machines.filter((m) => !m.assignment).length} workbenches`} onPress={() => go('code', 'compute')} />
              <Stat tone={tone} icon={<Shield />} label="Policy" value={d.policy ? d.policy.name : '—'} detail={d.policy ? `Revision ${d.policy.revision}` : 'Organisation default'} onPress={() => go('rules')} />
            </div>
          )}
          <PartBoundary key={`${tab}/${part}`} name={part.charAt(0).toUpperCase() + part.slice(1)} tone={tone}>

          {tab === 'overview' && part === 'summary' && (
            <>
              <ProjectPurpose projectId={projectId} purpose={d.project.purpose} roadmapPurpose={d.roadmapPurpose} owner={owner && d.project.state !== 'archived'} onChange={data.reload} />
              <ProjectInbox projectId={projectId} projectName={d.project.name} needsYouOnly />
              <WorkingNow projectId={projectId} owner={owner} me={account?.id} />
              {!isCloud() && <ProjectBudgetLine projectId={projectId} owner={owner} />}
              {!d.project.businessAreaId && owner && !isCloud() && <Promote projectId={projectId} pending={d.promotion} onChange={data.reload} />}
              {!isCloud() && <A2aAgentsPanel projectId={projectId} />}
              {owner && <ProjectLifecycle projectId={projectId} name={d.project.name} state={d.project.state ?? 'active'} archivedAt={d.project.archivedAt} admin={admin.data === true} onChange={data.reload} onRemoved={() => navigate('/projects')} />}
            </>
          )}
          {tab === 'overview' && part === 'inbox' && <ProjectInbox projectId={projectId} projectName={d.project.name} />}
          {tab === 'overview' && part === 'history' && (
            <ProjectHistory
              projectId={projectId}
              roots={d.roots}
              runId={params.get('run') ?? undefined}
              onRun={(run) => setParams({ ...projectTabParams('overview', 'history'), ...(run ? { run } : {}) }, { replace: true })}
            />
          )}
          {tab === 'overview' && part === 'activity' && (
            <>
              <ProjectRuns projectId={projectId} />
              <CloudRuns tone={tone} projectId={projectId} />
              <Activity projectId={projectId} />
            </>
          )}

          {tab === 'work' && part === 'board' && <Work projectId={projectId} hasSource={Boolean(source.data)} owner={owner} canWrite={d.role !== 'viewer'} />}
          {tab === 'work' && (part === 'tasks' || part === 'plans') && <ProjectBoard projectId={projectId} part={part} canWrite={d.role !== 'viewer'} />}
          {tab === 'work' && part === 'teamwork' && <TeamworkList projectId={projectId} />}
          {tab === 'work' && part === 'map' && <ProjectMapPanel projectId={projectId} canEdit={d.role !== 'viewer'} />}
          {tab === 'work' && part === 'risks' && <ProjectRisks projectId={projectId} canWrite={d.role !== 'viewer'} roots={rootIds} />}
          {tab === 'work' && part === 'incidents' && <ProjectIncidents projectId={projectId} canWrite={d.role !== 'viewer'} />}

          {tab === 'people' && (
            <>
              <Members key={JSON.stringify(d.members)} projectId={projectId} members={d.members} owner={owner} onChange={data.reload} />
              <Teams projectId={projectId} projectName={d.project.name} canWrite={d.role !== 'viewer'} owner={owner} allowPersonal={d.project.allowPersonalTeams} onChange={data.reload} />
            </>
          )}

          {tab === 'code' && part === 'repositories' && (
            <>
              <Repositories projectId={projectId} classification={d.project.classification} roots={d.roots} owner={owner} source={source.data ?? null} me={account?.id} onChange={data.reload} onSourceChange={source.reload} />
              {!isCloud() && <TrackersPanel projectId={projectId} source={source.data ?? null} owner={owner} onChange={source.reload} />}
            </>
          )}
          {tab === 'code' && part === 'history' && <CodeHistory projectId={projectId} roots={d.roots} source={source.data ?? null} owner={owner} onConnect={() => go('code', 'repositories')} />}
          {tab === 'code' && part === 'compute' &&<ProjectCompute projectId={projectId} owner={owner} roots={d.roots} compute={d.compute} me={account?.id} />}

          {tab === 'memory' && (
            <>
              {part === 'knowledge' && <KnowledgePanel projectId={projectId} canWrite={d.role !== 'viewer'} onOpenIndexItem={(item) => (setParams({ ...projectTabParams('memory', 'browse'), item }), window.scrollTo({ top: 0 }))} />}
              {part === 'browse' && (
                <>
                  {params.get('item') && <OpenedMemoryItem projectId={projectId} itemId={params.get('item')!} owner={owner} onClose={() => go('memory', 'browse')} />}
                  <MemoryAccessNote key={d.project.classification ?? 'unclassified'} projectId={projectId} owner={owner} onClassified={data.reload} />
                  <ProjectMemoryPanel projectId={projectId} />
                  <MemoryBrowser projectId={projectId} owner={owner} />
                </>
              )}
              {part === 'explore' && (
                <MemoryExplorer projectId={projectId} projectName={d.project.name} rootNames={rootNames} onOpenItem={(item) => (setParams({ ...projectTabParams('memory', 'browse'), item }), window.scrollTo({ top: 0 }))} />
              )}
              {part === 'labels' && <LabellingRulesForm projectId={projectId} owner={owner} />}
              {part === 'readers' && <ProjectReaders projectId={projectId} owner={owner} members={d.members} />}
              {part === 'sharing' &&
                (owner ? (
                  <ShareMemory projectId={projectId} mirror={d.project.memoryMirror} onChange={data.reload} />
                ) : (
                  <Empty tone={tone} icon={<FolderArrowUp />} title={d.project.memoryMirror?.enabled ? 'Shared with the organisation' : 'Not shared with the organisation'} body="The project's owners decide what of its memory the organisation's memory holds. A shared copy is never labelled lower than here." />
                ))}
            </>
          )}

          {tab === 'rules' && (
            <>
              <ListGroup title="Policy" description="The rules every workbench and agent on this project works under.">
                <ListRow
                  icon={<Shield />}
                  title={d.policy ? d.policy.name : 'The organisation default'}
                  description={d.policy ? `Revision ${d.policy.revision}. This policy names the project.` : 'It applies unless a policy names this project.'}
                  to={d.policy ? `/policies/${encodeURIComponent(d.policy.id)}` : '/policies'}
                />
              </ListGroup>
              <ClassificationPanel key={`${d.project.classification}/${d.roots.map((r) => r.classification).join()}`} projectId={projectId} classification={d.project.classification} roots={uniqueRoots(d.roots)} owner={owner} onChange={data.reload} />
              <MergeRulesPanel key={JSON.stringify(d.project.pullRequestPolicy)} projectId={projectId} policy={d.project.pullRequestPolicy} owner={owner} onChange={data.reload} />
              <ProjectSettingsPolicy key={JSON.stringify(d.project.settingsPolicy)} projectId={projectId} value={d.project.settingsPolicy} owner={owner} onChange={data.reload} />
              <ProjectTools projectId={projectId} />
              <SecretsPanel tone={tone} where={{ projectId, roots: rootIds }} />
            </>
          )}
          </PartBoundary>
        </>
      )}
    </PageColumn>
  );
  return d ? (
    <SubnavLayout label="Project" groups={projectNav(projectId)} current={projectNavKey(tab, part)}>
      {page}
    </SubnavLayout>
  ) : (
    page
  );
}

function Members({ projectId, members, owner, onChange }: { projectId: string; members: ProjectMember[]; owner: boolean; onChange: () => void }) {
  const tone = VIEW_TONES.projects!;
  const [draft, setDraft] = useState(members);
  const [role, setRole] = useState<ProjectMember['role']>('contributor');
  const [error, setError] = useState<string>();
  const dirty = JSON.stringify(draft) !== JSON.stringify(members);
  const save = async () => {
    setError(undefined);
    try {
      await routes.setProjectMembers(projectId, draft.map(({ email: _e, picture: _p, ...m }) => m));
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <ListGroup
      title="Members"
      count={draft.length || undefined}
      description={`${groupsAvailable() ? 'People and directory groups' : 'People'}, with a role each.${owner ? ' Owners decide everything about the project; contributors work on it; viewers read it.' : ''}`}
      empty={draft.length === 0 && !owner ? <Empty tone={tone} icon={<Persons />} compact title="No members yet" body={groupsAvailable() ? "The project's owners add people and groups here." : "The project's owners add people here."} /> : undefined}
    >
      {draft.map((m, i) => (
        <ListRow
          key={`${m.subjectType}/${m.subjectId}`}
          icon={m.subjectType === 'user' ? <PersonAvatar name={m.name ?? m.email ?? '?'} picture={m.picture} /> : <Persons />}
          title={<SubjectName id={m.subjectId} fallback={m.email} />}
          description={m.subjectType === 'group' ? 'Group' : 'Person'}
          {...(owner
            ? {
                control: (
                  <>
                    <ChoiceSelect label={`Role of ${m.name ?? m.email ?? m.subjectId}`} value={m.role} options={ROLE_TEXT as Record<ProjectMember['role'], string>} onChange={(r) => setDraft(draft.map((x, j) => (j === i ? { ...x, role: r } : x)))} />
                    <RowMenu label={`More for ${m.name ?? m.email ?? m.subjectId}`} items={[{ id: 'remove', label: 'Remove from the project', danger: true }]} onAction={() => setDraft(draft.filter((_, j) => j !== i))} />
                  </>
                ),
              }
            : { value: ROLE_TEXT[m.role] })}
        />
      ))}
      {owner ? (
        <ListRow
          title="Add a member"
          description={groupsAvailable() ? 'Find a person or a directory group, and the role they join with. Everyone in a group has its role.' : 'Find a person, and the role they join with.'}
          control={
            <ChoiceSelect label="Role for the next member" value={role} options={{ contributor: 'Contributor', viewer: 'Viewer', owner: 'Owner' }} onChange={setRole} />
          }
        >
          <SubjectPicker
            className="w-full"
            kinds={['user', 'group']}
            placeholder="Add a person or group by name"
            onPick={(s) => setDraft([...draft.filter((m) => !(m.subjectType === s.type && m.subjectId === s.id)), { subjectType: s.type, subjectId: s.id, role, email: s.detail }])}
          />
        </ListRow>
      ) : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {owner && dirty ? (
        <ListBlock className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onPress={() => setDraft(members)}>
            Discard
          </Button>
          <Button size="sm" variant="primary" onPress={() => void save()}>
            Save members
          </Button>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** What happened in the project, as sentences signed by the person each write was for; the audit trail beneath. */
function Activity({ projectId }: { projectId: string }) {
  const tone = VIEW_TONES.projects!;
  const log = useLoad(() => routes.projectActivity(projectId), [projectId], 30_000);
  const entries = [...(log.data?.entries ?? [])].reverse();
  const agentName = useAgentName();
  const who = (e: (typeof entries)[number]) => (e.by.principal ? (log.data?.people[e.by.principal]?.name ?? <SubjectName id={e.by.principal} />) : e.by.actor === 'rollup' ? 'The roll-up' : (agentName(e.by.actor) ?? 'The organisation'));
  return (
    <>
    <ListGroup
      title="What happened"
      count={log.data?.seq || undefined}
      description="Newest first: roadmap, architecture and board changes on the project's own layer, each signed by the person it was for."
      empty={log.data && entries.length === 0 ? <Empty tone={tone} icon={<ClockArrowRotateLeft />} title="Nothing on the project's own layer yet" body="Changes to the project roadmap, the project architecture and the boards show here as they are made, each signed by the person it was for." compact /> : undefined}
    >
      {log.error ? (
        <ListBlock>
          <ErrorNote error={log.error} />
        </ListBlock>
      ) : null}
      {!log.data && !log.error ? <ListBlock className="text-sm text-muted">Reading what happened…</ListBlock> : null}
      {entries.map((e) => (
        <ListRow
          key={e.seq}
          title={
            <>
              {who(e)}
              {e.by.principal && e.by.actor ? <span className="font-normal text-muted"> ({agentName(e.by.actor)})</span> : null} <span className="font-normal">{e.text}</span>
            </>
          }
          description={`${e.rootId === '' ? 'Project' : e.rootId} · ${timeAgo(e.at)}`}
        />
      ))}
    </ListGroup>
    <AuditTrail projectId={projectId} />
    </>
  );
}

/** What people and their agents did in the project's repositories, as a table of compared columns. */
function AuditTrail({ projectId }: { projectId: string }) {
  const tone = VIEW_TONES.projects!;
  const rows = useLoad(() => routes.projectAudit(projectId), [projectId], 30_000);
  return (
    <ListGroup
      title="Audit trail"
      count={rows.data?.length || undefined}
      description="What people and their agents did in this project's repositories, with who asked for it."
      empty={rows.data && rows.data.length === 0 ? <Empty tone={tone} icon={<FileText />} compact title="No activity yet" body="What people and their agents do in this project's repositories shows here, with who asked for it." /> : undefined}
    >
      {rows.error ? (
        <ListBlock>
          <ErrorNote error={rows.error} />
        </ListBlock>
      ) : null}
      {!rows.data && !rows.error ? <ListBlock className="text-sm text-muted">Reading the audit trail…</ListBlock> : null}
      {rows.data?.length ? (
        <ListBlock flush>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-24 @[40rem]:w-28">When</th>
                  <th>What</th>
                  <th className="hidden @[40rem]:table-cell">Person</th>
                  <th className="hidden @[56rem]:table-cell">Agent</th>
                  <th className="w-28">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.map((a, i) => {
                  const said = auditSentence(a);
                  const person = a.actor.principal ? <SubjectName id={a.actor.principal} /> : a.actor.kind === 'user' && a.actor.id ? <SubjectName id={a.actor.id} /> : '—';
                  return (
                    <tr key={`${a.ts}/${i}`}>
                      <td className="whitespace-nowrap text-muted">{timeAgo(a.ts)}</td>
                      <td>
                        {/* Narrow: the person goes above the sentence, so the sentence keeps the width. */}
                        <span className="data-table__meta truncate @[40rem]:hidden">{person}</span>
                        {said ? <span>{said}</span> : <span className="mono text-xs">{a.action}</span>}
                        {said ? (
                          <span className="data-table__meta truncate">
                            <span className="mono">{a.action}</span>
                            {a.target ? ` · ${a.target}` : ''}
                          </span>
                        ) : (
                          a.target && <span className="data-table__meta truncate">{a.target}</span>
                        )}
                      </td>
                      <td className="hidden truncate @[40rem]:table-cell">{person}</td>
                      <td className="hidden truncate text-muted @[56rem]:table-cell">
                        {a.actor.kind === 'agent' ? (a.actor.agentName ?? a.actor.id) : a.actor.kind}
                        {a.taskId ? ` · ${a.taskId}` : ''}
                      </td>
                      <td>
                        <StatusMark state={a.outcome === 'ok' ? 'online' : a.outcome === 'denied' ? 'medium' : 'error'} label={a.outcome === 'ok' ? 'Done' : a.outcome === 'denied' ? 'Denied' : 'Failed'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}


/** Labelling rules the project's nodes apply, and what a member would be allowed to read. */
/**
 * The project's repositories in one part: the provider connection as the header line, then each repository (its name, remote,
 * the machines that hold it and the trackers that mirror it). The connection and the provider's list are the former Source part.
 */
function Repositories({ projectId, classification, roots, source, owner, me, onChange, onSourceChange }: { projectId: string; roots: Awaited<ReturnType<typeof routes.project>>['roots']; source: ProjectSource | null; owner: boolean; me?: string; classification?: import('@aioe/shared').Classification; onChange: () => void; onSourceChange: () => void }) {
  const tone = VIEW_TONES.projects!;
  const connections = useLoad(() => routes.sourceConnections().catch(() => []), []);
  const agentFiles = useLoad(() => routes.projectAgentFiles(projectId).catch(() => undefined), [projectId]);
  const [connectionId, setConnectionId] = useState(source?.connectionId ?? '');
  const [devopsProject, setDevopsProject] = useState(source?.devopsProject ?? '');
  const [repos, setRepos] = useState<SourceRepository[]>(source?.repositories ?? []);
  const [available, setAvailable] = useState<SourceRepository[]>();
  const [readAs, setReadAs] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const connection = (connections.data ?? []).find((c) => c.id === connectionId);
  const dirty = JSON.stringify({ connectionId, devopsProject, repos }) !== JSON.stringify({ connectionId: source?.connectionId ?? '', devopsProject: source?.devopsProject ?? '', repos: source?.repositories ?? [] });
  const list = async () => {
    if (!connection) return;
    setBusy(true);
    setError(undefined);
    try {
      const r = await routes.sourceRepos(connection.id, connection.provider === 'azure-devops' ? devopsProject.trim() : undefined);
      setAvailable(r.repositories);
      setReadAs(r.as);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setError(undefined);
    try {
      if (!connection) await routes.setProjectSource(projectId, null);
      else await routes.setProjectSource(projectId, { provider: connection.provider, connectionId: connection.id, organisation: connection.organisation, devopsProject: connection.provider === 'azure-devops' ? devopsProject.trim() || undefined : undefined, repositories: repos, trackers: source?.trackers ?? [] });
      onSourceChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const has = (r: SourceRepository) => repos.some((x) => x.name === r.name);
  const toggle = (r: SourceRepository) => setRepos(has(r) ? repos.filter((x) => x.name !== r.name) : [...repos, { ...r, primary: repos.length === 0 }]);
  const setPrimary = (name: string) => setRepos(repos.map((x) => ({ ...x, primary: x.name === name })));
  const names = [...new Map(roots.map((r) => [r.rootId, r])).values()];
  const byRoot = (rootId: string) => roots.filter((r) => r.rootId === rootId);
  const onNode = roots.some((r) => r.nodeId);
  const makeCanonical = async (rootId: string, nodeId?: string) => {
    setError(undefined);
    try {
      await routes.putProjectRoot(projectId, rootId, { nodeId, canonical: true });
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      <ListGroup
        title="Repositories"
        count={names.length || undefined}
        description="Each repository of the project, the machines that hold it and the trackers that mirror it. A workbench or a node that opens one reports it here."
        empty={names.length === 0 ? <Empty tone={tone} icon={<CodeTrunk />} compact title="No repositories yet" body="A workbench or a node that opens a repository of this project reports it here." /> : undefined}
      >
        {names.map((r) => {
          const match = repositoryForRemote(source ?? undefined, r.remote);
          const trackers = (source?.trackers ?? []).filter((t) => t.board.rootId === r.rootId);
          const nodeRows = byRoot(r.rootId).filter((x) => x.nodeId);
          const own = ownClassification(r, classification);
          return (
            <ListRow
              key={r.rootId}
              icon={<CodeTrunk />}
              title={<RootName projectId={projectId} rootId={r.rootId} name={r.name ?? r.rootId} owner={owner} onChange={onChange} />}
              description={
                <>
                  <span className={`block truncate ${r.remote ? 'mono' : ''}`} title={r.remote}>
                    {r.remote ? `${r.remote}${r.branch ? ` (${r.branch})` : ''}` : 'No remote reported'}
                  </span>
                  {match || own ? <span className="block">{[match ? `${match.name}${match.primary ? ', the primary at the provider' : ' at the provider'}` : '', own ?? ''].filter(Boolean).join(' · ')}</span> : null}
                </>
              }
              {...(owner ? { compactControl: true, control: <RemoveRoot projectId={projectId} rootId={r.rootId} name={r.name ?? r.rootId} onChange={onChange} /> } : {})}
            >
              <div className="grid gap-1.5 text-sm">
                <RepositoryMachines
                  holders={r.holders}
                  me={me}
                  {...(onNode
                    ? {
                        attach: {
                          /* Only a node's clone can be attached to: members then get hosts:attach and memory:read on that node. A member's own machine is theirs. */
                          clone: (id: string) => nodeRows.some((n) => n.nodeId === id),
                          canonical: (id: string) => nodeRows.some((n) => n.nodeId === id && n.canonical),
                          ...(owner ? { onAllow: (id: string) => void makeCanonical(r.rootId, id) } : {}),
                        },
                      }
                    : {})}
                />
                <RepositoryAgentFiles files={agentFiles.data?.roots.find((f) => f.rootId === r.rootId)} />
                {trackers.length > 0 && <p className="text-xs text-muted">Mirrored by {trackers.map((t) => `${t.provider === 'github-issues' ? 'GitHub Issues' : t.provider === 'jira' ? 'Jira' : 'Azure Boards'}${t.board.board ? ` (${t.board.board} board)` : ''}`).join(', ')}</p>}
              </div>
            </ListRow>
          );
        })}
      </ListGroup>

      <ListGroup
        title="At the provider"
        description={source ? `${PROVIDER_TEXT[source.provider]} · ${source.organisation}${source.devopsProject ? ` / ${source.devopsProject}` : ''}. Open issues, pull requests and work items are read from there.` : 'Where the project’s repositories live. Open issues, pull requests and work items are read from there.'}
      >
        {owner ? (
          <ListRow
            title="Connection"
            description={(connections.data ?? []).length === 0 ? 'No connection yet: an administrator adds one under Settings, Source connections.' : readAs ? `Listed with ${readAs === 'organisation' ? "the organisation's token" : 'your own account'}.` : 'Where the project’s repositories live.'}
            control={
              <>
                <ChoiceSelect
                  label="Connection"
                  className="w-fit min-w-44 max-w-full"
                  value={connectionId || NONE}
                  options={{ [NONE]: 'No source', ...Object.fromEntries((connections.data ?? []).map((c) => [c.id, `${c.name} · ${PROVIDER_TEXT[c.provider]} · ${c.organisation}`])) }}
                  onChange={(v) => {
                    setConnectionId(v === NONE ? '' : v);
                    setAvailable(undefined);
                  }}
                />
                {connection && (
                  <Button size="sm" variant="secondary" isPending={busy} isDisabled={connection.provider === 'azure-devops' && !devopsProject.trim()} onPress={() => void list()}>
                    List repositories
                  </Button>
                )}
              </>
            }
          >
            {connection?.provider === 'azure-devops' ? <input aria-label="DevOps project" className={`${inputClass} max-w-xs`} value={devopsProject} onChange={(e) => setDevopsProject(e.target.value)} placeholder="DevOps project" /> : null}
          </ListRow>
        ) : null}
        {available ? (
          <ListBlock>
            <p className="m-0 mb-2 text-xs text-muted">Tick the repositories that are this project’s.</p>
            <ul className="m-0 grid list-none gap-1.5 p-0 text-sm sm:grid-cols-2">
              {available.map((r) => (
                <li key={r.name}>
                  <Tick checked={has(r)} onChange={() => toggle(r)}>
                    <span>
                      {r.name} <span className="text-muted">· {r.defaultBranch}</span>
                    </span>
                  </Tick>
                </li>
              ))}
              {available.length === 0 && <li className="text-muted">Nothing the credential can see.</li>}
            </ul>
          </ListBlock>
        ) : null}
        {owner
          ? repos.map((r) => (
              <ListRow
                key={r.name}
                title={r.name}
                description={<span className="mono block truncate">{r.remote}</span>}
                compactControl
                control={
                  <>
                    <label className="inline-flex items-center gap-1.5 text-xs">
                      <input type="radio" name="primary" className="accent-[var(--accent)]" checked={r.primary} onChange={() => setPrimary(r.name)} />
                      Primary
                    </label>
                    <RowMenu label={`More for ${r.name}`} items={[{ id: 'remove', label: 'Drop from the project', danger: true }]} onAction={() => setRepos(repos.filter((x) => x.name !== r.name))} />
                  </>
                }
              />
            ))
          : null}
        {!owner ? <ListRow title={source ? PROVIDER_TEXT[source.provider] : 'No provider connected'} description={source ? `${source.repositories.length} ${source.repositories.length === 1 ? 'repository' : 'repositories'} chosen there.` : 'The project’s owners connect one.'} /> : null}
        {error || connections.error ? (
          <ListBlock>
            <ErrorNote error={error ?? connections.error} />
          </ListBlock>
        ) : null}
        {owner && dirty ? (
          <ListBlock className="flex justify-end">
            <Button size="sm" variant="primary" onPress={() => void save()}>
              Save source
            </Button>
          </ListBlock>
        ) : null}
      </ListGroup>
    </>
  );
}

const WORK_KIND: Record<WorkItem['kind'], string> = { issue: 'Issue', 'pull-request': 'Pull request', 'work-item': 'Work item' };

/** Open issues, pull requests and work items at the provider, read with the viewer's own account. */
/** An owner asks for the project to be filed under a business area; the area's managers decide. */
function Promote({ projectId, pending, onChange }: { projectId: string; pending?: Promotion; onChange: () => void }) {
  const areas = useLoad(() => routes.areas(), []);
  const [area, setArea] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string>();
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await f();
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const pendingArea = pending && (areas.data?.find((a) => a.id === pending.businessAreaId)?.name ?? pending.businessAreaId);
  return (
    <ListGroup title="File under a business area" description="This project is under no business area yet. Filed under one, the area's managers own it with you, it shows in the portfolio, and nothing moves.">
      {pending ? (
        <ListRow
          icon={<FolderArrowUp />}
          title={`Waiting for ${pendingArea}'s managers`}
          description={`Asked ${timeAgo(pending.requestedAt)}. They accept or decline it.`}
          compactControl
          control={
            <Button size="sm" variant="secondary" onPress={() => void act(() => routes.decidePromotion(pending.id, 'withdraw'))}>
              Withdraw
            </Button>
          }
        />
      ) : (areas.data?.length ?? 0) === 0 ? (
        <ListRow icon={<FolderArrowUp />} title="No business areas yet" description="A portfolio manager creates them under Portfolio." />
      ) : (
        <>
          <ListRow
            title="Business area"
            description="Its managers decide whether to take the project."
            control={<ChoiceSelect label="Business area" value={area || undefined} placeholder="Choose an area" options={Object.fromEntries((areas.data ?? []).map((a) => [a.id, a.name]))} onChange={setArea} />}
          />
          <ListRow title="A note for the managers" htmlFor={`promote-note-${projectId}`} description="Optional: why it belongs there.">
            <input id={`promote-note-${projectId}`} className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why it belongs there" />
          </ListRow>
          <ListBlock className="flex justify-end">
            <Button size="sm" variant="primary" isDisabled={!area} onPress={() => void act(() => routes.promoteProject(projectId, { businessAreaId: area, ...(note.trim() ? { note: note.trim() } : {}) }))}>
              Ask to file it
            </Button>
          </ListBlock>
        </>
      )}
      {error || areas.error ? (
        <ListBlock>
          <ErrorNote error={error ?? areas.error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** The project's teams and specialists, with their members set up in full: owners and contributors edit them here (D251); and whether members may run their own. */
function Teams({ projectId, projectName, owner, canWrite, allowPersonal, onChange }: { projectId: string; projectName: string; owner: boolean; canWrite: boolean; allowPersonal: boolean; onChange: () => void }) {
  const tone = VIEW_TONES.projects!;
  const [error, setError] = useState<string>();
  const toggle = async () => {
    setError(undefined);
    try {
      await routes.setProjectSettings(projectId, { allowPersonalTeams: !allowPersonal });
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      <ListGroup title="Teams" description="Enterprise teams granted to this project. Workbenches load the granted ones read-only.">
        <ListRow
          icon={<PersonsLock />}
          title="Members' own teams and specialists"
          description={allowPersonal ? 'Members may run their own teams and specialists here too.' : 'Only the enterprise teams granted to this project run on it; a workbench refuses any other.'}
          {...(owner ? { control: <Toggle label="Members' own teams and specialists" checked={allowPersonal} onChange={() => void toggle()} /> } : { value: allowPersonal ? 'Allowed' : 'Not allowed' })}
        />
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ProjectTeamsList projectId={projectId} canEdit={canWrite} projectName={projectName} empty={<Empty tone={tone} icon={<PersonsLock />} compact title="No teams yet" body="Enterprise teams granted to this project, and members' own teams when they are allowed, show here with their members." />} />
    </>
  );
}

/** The project's shared boards: where its members' workbenches keep their tasks. */
function Boards({ projectId }: { projectId: string }) {
  const tone = VIEW_TONES.projects!;
  const boards = useLoad(() => routes.projectBoards(projectId), [projectId]);
  const rows = boards.data ?? [];
  return (
    <ListGroup
      title="Shared boards"
      count={rows.length || undefined}
      description="Task boards the members' workbenches share, by repository and team."
      empty={boards.data && rows.length === 0 ? <Empty tone={tone} icon={<LayoutColumns />} compact title="No shared board yet" body="A workbench that keeps this project's board in the organisation creates one with its first task." /> : undefined}
    >
      {boards.error ? (
        <ListBlock>
          <ErrorNote error={boards.error} />
        </ListBlock>
      ) : null}
      {!boards.data && !boards.error ? <ListBlock className="text-sm text-muted">Reading the boards…</ListBlock> : null}
      {rows.length ? (
        <ListBlock flush>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Board</th>
                  <th className="data-table__end w-20">Open</th>
                  <th className="data-table__end w-20">Tasks</th>
                  <th className="hidden w-28 @[40rem]:table-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={`${b.rootId}/${b.board}`}>
                    <td>
                      <span className="font-medium">{b.rootId === '' ? 'Project board' : b.board === 'solo' ? 'Solo work' : b.board}</span>
                      <span className={`data-table__meta truncate ${b.rootId === '' ? '' : 'mono'}`}>{b.rootId === '' ? 'Above the repositories: tasks delegated to theirs' : b.rootId}</span>
                    </td>
                    <td className="data-table__end">{b.open}</td>
                    <td className="data-table__end">{b.tasks}</td>
                    <td className="hidden whitespace-nowrap text-muted @[40rem]:table-cell">{b.updatedAt ? timeAgo(b.updatedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

function Work({ projectId, hasSource, owner, canWrite }: { projectId: string; hasSource: boolean; owner: boolean; canWrite: boolean }) {
  const { account } = useAuth();
  return (
    <>
      <QueuePanel projectId={projectId} owner={owner} me={account?.id} />
      <PullRequestsPanel projectId={projectId} canWrite={canWrite} />
      <Boards projectId={projectId} />
      {!isCloud() && <WorkCosts projectId={projectId} />}
      <OpenWork projectId={projectId} hasSource={hasSource} />
    </>
  );
}

function OpenWork({ projectId, hasSource }: { projectId: string; hasSource: boolean }) {
  const tone = VIEW_TONES.projects!;
  const work = useLoad<{ items: WorkItem[]; as?: string; cached?: boolean }>(() => (hasSource ? routes.projectWork(projectId) : Promise.resolve({ items: [] })), [projectId, hasSource]);
  const items = work.data?.items ?? [];
  return (
    <ListGroup
      title="Open work"
      count={items.length || undefined}
      description={`Open issues, pull requests and work items at the provider${work.data?.as ? `, read as ${work.data.as === 'organisation' ? 'the organisation' : 'you'}` : ''}.`}
      actions={
        hasSource ? (
          <Button size="sm" variant="ghost" onPress={() => void routes.projectWork(projectId, true).then(work.reload)}>
            Refresh
          </Button>
        ) : undefined
      }
      empty={
        !hasSource ? (
          <Empty tone={tone} icon={<ListCheck />} compact title="No source yet" body="Once the project names its repositories, open issues, pull requests and work items show here." />
        ) : work.data && items.length === 0 ? (
          <Empty tone={tone} icon={<ListCheck />} compact title="Nothing open" body="No issue, pull request or work item is open at the provider." />
        ) : undefined
      }
    >
      {work.error ? (
        <ListBlock>
          <ErrorNote error={work.error} />
        </ListBlock>
      ) : null}
      {!work.data && !work.error ? <ListBlock className="text-sm text-muted">Asking the provider…</ListBlock> : null}
      {items.length ? (
        <ListBlock flush>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="hidden w-28 @[40rem]:table-cell">Kind</th>
                  <th>Title</th>
                  <th className="hidden w-40 @[48rem]:table-cell">Who</th>
                  <th className="w-24">Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id}>
                    <td className="hidden text-muted @[40rem]:table-cell">{WORK_KIND[w.kind]}</td>
                    <td>
                      <a href={w.url} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline">
                        {w.title}
                      </a>
                      <span className="data-table__meta truncate">
                        <span className="@[40rem]:hidden">{WORK_KIND[w.kind]} · </span>
                        {w.id}
                        {w.labels.length ? ` · ${w.labels.join(', ')}` : ''}
                      </span>
                    </td>
                    <td className="hidden truncate text-muted @[48rem]:table-cell">{w.assignee ?? w.author ?? '—'}</td>
                    <td className="whitespace-nowrap text-muted">{w.updatedAt ? timeAgo(w.updatedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
